import { apiClient } from '../apiClient';

/**
 * Helper to extract company slug from current URL path
 * CRITICAL: Returns empty string if company not found to prevent malformed URLs
 */
const getCompanySlug = (): string => {
  if (typeof window === 'undefined') return '';
  const parts = window.location.pathname.split('/').filter(Boolean);
  const company = parts[0] || '';
  
  // CRITICAL FIX: Validate company slug to prevent empty/malformed URLs
  // Empty company would result in /api//rpc/agents/list which causes 404
  if (!company || company === 'undefined' || company.trim() === '') {
    console.warn('[rpcAgents] Company slug is empty or invalid:', {
      pathname: window.location.pathname,
      parts,
    });
    return '';
  }
  
  return company;
};

/**
 * Build RPC endpoint URL
 * CRITICAL: Validates company before building URL to prevent 404s
 */
const buildUrl = (path: string): string => {
  const company = getCompanySlug();
  
  // CRITICAL FIX: Prevent API calls with empty company (would cause 404)
  if (!company) {
    const error = new Error('Company slug is required but not available. Please ensure you are on a valid company page.');
    console.error('[rpcAgents] buildUrl failed:', error.message, {
      pathname: window.location.pathname,
    });
    throw error; // Throw error to prevent malformed request
  }
  
  return `/api/${company}/rpc/agents${path}`;
};

/**
 * RPC client for agent-related operations
 * All endpoints return { ok: true, data } or { ok: false, error }
 */
const rpcAgents = {
  /**
   * List all agents
   */
  list(params?: { isBlocked?: boolean; isArchived?: boolean }) {
    return apiClient.post(buildUrl('/list'), params || {});
  },

  /**
   * Get agent by ID
   */
  getById(agentId: string) {
    return apiClient.post(buildUrl('/get'), { agentId });
  },

  /**
   * Create new agent
   */
  create(payload: { fullName: string; email: string; phone: string; password: string }) {
    return apiClient.post(buildUrl('/create'), payload);
  },

  /**
   * Update agent
   */
  update(agentId: string, payload: { fullName?: string; email?: string; phone?: string }) {
    return apiClient.post(buildUrl('/update'), { agentId, ...payload });
  },

  /**
   * Archive/unarchive agent (replaces delete)
   */
  archive(agentId: string, isArchived: boolean) {
    return apiClient.post(buildUrl('/archive'), { agentId, isArchived });
  },

  /**
   * Delete agent (deprecated - use archive instead)
   * @deprecated Use archive instead
   */
  delete(agentId: string) {
    return apiClient.post(buildUrl('/delete'), { agentId });
  },

  /**
   * Get agent statistics
   */
  getStats() {
    return apiClient.post(buildUrl('/get-stats'), {});
  },

  /**
   * Block/unblock agent
   */
  block(agentId: string, isBlocked: boolean) {
    return apiClient.post(buildUrl('/block'), { agentId, isBlocked });
  },

  /**
   * Change agent password
   */
  changePassword(agentId: string, newPassword: string) {
    return apiClient.post(buildUrl('/change-password'), { agentId, newPassword });
  },

  /**
   * List agent's clients (potential clients)
   */
  listClients(params: { page?: number; pageSize?: number }) {
    return apiClient.post(buildUrl('/clients/list'), params);
  },

  /**
   * Create a potential client for an agent
   */
  createClient(payload: {
    fullName: string;
    phone?: string;
    email: string;
    description?: string;
    business?: string;
    contacts?: any[];
    addedByAgent?: string;
  }) {
    return apiClient.post(buildUrl('/clients/create'), payload);
  },

  /**
   * Get a potential client by ID (agent scope)
   */
  getClientById(clientId: string) {
    return apiClient.post(buildUrl('/clients/get-by-id'), { clientId });
  },

  /**
   * Update a potential client (agent scope)
   */
  updateClient(clientId: string, payload: any) {
    return apiClient.post(buildUrl('/clients/update'), { clientId, payload });
  },

  /**
   * Delete a potential client (agent scope)
   */
  deleteClient(clientId: string) {
    return apiClient.post(buildUrl('/clients/delete'), { clientId });
  },

  /**
   * Update status of a potential client
   */
  updateClientStatus(clientId: string, status: string) {
    return apiClient.post(buildUrl('/clients/update-status'), { clientId, status });
  },

  /**
   * List notes for a potential client (agent scope)
   */
  listClientNotes(clientId: string) {
    return apiClient.post(buildUrl('/clients/notes/list'), { clientId });
  },

  /**
   * Create a note for a potential client (agent scope)
   */
  createClientNote(clientId: string, text: string) {
    return apiClient.post(buildUrl('/clients/notes/create'), { clientId, text });
  },

  /**
   * Update a note for a potential client (agent scope)
   */
  updateClientNote(clientId: string, noteIdx: number, text: string) {
    return apiClient.post(buildUrl('/clients/notes/update'), { clientId, noteIdx, text });
  },

  /**
   * Delete a note for a potential client (agent scope)
   */
  deleteClientNote(clientId: string, noteIdx: number) {
    return apiClient.post(buildUrl('/clients/notes/delete'), { clientId, noteIdx });
  },

  /**
   * Get agent performance stats
   */
  getPerformance(params: { page?: number; limit?: number }) {
    return apiClient.post(buildUrl('/performance'), params);
  },

  // ==================== ADMIN-SPECIFIC METHODS ====================

  /**
   * List all agents (admin scope)
   */
  listAdmin() {
    return apiClient.post(buildUrl('/admin/list-agents'), {});
  },

  /**
   * Get agent stats (admin scope)
   */
  getAdminStats() {
    return apiClient.post(buildUrl('/admin/get-agent-stats'), {});
  },

  /**
   * Get agents performance analytics (admin scope)
   */
  getAdminPerformance(params?: { page?: number; limit?: number }) {
    return apiClient.post(buildUrl('/admin/agents-performance'), params || {});
  },

  /**
   * List agent's clients (admin scope)
   */
  listAdminClients(params?: { agentId?: string; page?: number; pageSize?: number }) {
    return apiClient.post(buildUrl('/admin/clients/list'), { params });
  },

  /**
   * Get agent client by ID (admin scope)
   */
  getAdminClientById(clientId: string) {
    return apiClient.post(buildUrl('/admin/clients/get-by-id'), { clientId });
  },

  /**
   * Create agent client (admin scope)
   */
  createAdminClient(payload: any) {
    return apiClient.post(buildUrl('/admin/clients/create'), payload);
  },

  /**
   * Update agent client (admin scope)
   */
  updateAdminClient(clientId: string, payload: any) {
    return apiClient.post(buildUrl('/admin/clients/update'), { clientId, payload });
  },

  /**
   * Delete agent client (admin scope)
   */
  deleteAdminClient(clientId: string) {
    return apiClient.post(buildUrl('/admin/clients/delete'), { clientId });
  },

  /**
   * Block/unblock agent client (admin scope)
   */
  blockAdminClient(clientId: string, isBlocked: boolean) {
    return apiClient.post(buildUrl('/admin/clients/block'), { clientId, isBlocked });
  },

  /**
   * Update agent client status (admin scope)
   */
  updateAdminClientStatus(clientId: string, status: string) {
    return apiClient.post(buildUrl('/admin/clients/update-status'), { clientId, status });
  },

  /**
   * Transfer agent client (admin scope)
   */
  transferAdminClient(clientId: string, newAgentId: string) {
    return apiClient.post(buildUrl('/admin/clients/transfer'), { clientId, newAgentId });
  },

  /**
   * Convert agent client (admin scope)
   */
  convertAdminClient(clientId: string) {
    return apiClient.post(buildUrl('/admin/clients/convert'), { clientId });
  },
};

export default rpcAgents;

